import { normalizeCode } from './guests'

export type Route =
  | { page: 'console' }
  /** `code` is a code lifted from the path, for direct links on invitations. */
  | { page: 'guest'; code: string | null }

/**
 * Works out which page the current URL wants, and whether it carries a code.
 *
 * There are two pages and no navigation between them, so the path is read once
 * rather than pulling in a router. A single path segment that is not one of ours
 * is taken as an invite code, which is what makes `/HAPPYDAY` printed on an
 * invitation work without anybody typing anything.
 *
 * Deliberately not fussy about what looks like a code: it gets handed to the
 * same sign-in a typed code goes through, and the server is the only thing that
 * actually knows. A wrong guess costs one rejected attempt and drops the guest at
 * the prompt with the code already in the box.
 *
 * Our own routes win. Anything added to the app has to be listed here before it
 * goes live, or a guest whose code collides with it lands on the wrong page.
 */
export function readRoute(pathname: string = window.location.pathname): Route {
  const segments = pathname.split('/').filter((segment) => segment !== '')

  // Only ever one segment: nothing nested is a code, and /api and /assets are
  // answered by the server before they can reach this.
  if (segments.length !== 1) return { page: 'guest', code: null }

  const only = segments[0]!
  if (only.toLowerCase() === 'console') return { page: 'console' }

  const code = normalizeCode(safelyDecode(only))
  return { page: 'guest', code: code === '' ? null : code }
}

/**
 * Takes the code back out of the address bar once it has been accepted.
 *
 * The session is a cookie, so the code has done its job by then, and leaving it
 * on display only invites trouble: it is the one credential a guest has, and it
 * would otherwise sit in the URL for every screenshot and shared screen for the
 * rest of their visit. `replaceState` rather than `pushState`, so Back does not
 * walk them into it again.
 */
export function forgetCodeInUrl(): void {
  window.history.replaceState({}, '', '/')
}

/** A malformed escape sequence is not worth throwing over. */
function safelyDecode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}
